import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRotate,
  faUpRightFromSquare,
  faExpand,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';

import { http } from '../../features/jobs/api/http';
import { API } from '../../config/api';
import { prepareHmiPage, hmiPagePath } from '../../helpers/hmiEmbed';

// The three Pi machines serve their own panel from their own box; everything
// else is served by machine_hmi_interface on the main host. Only the "open it
// on :5005" escape hatch needs this — the embedded panel is always read from
// the main host's copy.
const HMI_HOST = {
  l70: '192.168.1.172',
  dhc600: '192.168.1.238',
  ath600: '192.168.1.179'
};
const hmiDirectUrl = machineId => {
  const m = String(machineId || '').toLowerCase();
  return `http://${HMI_HOST[m] || '192.168.1.162'}:5005/${m}-hmi`;
};

// The operator panel, inside the app.
//
// Not a port and not a copy: the page served here is the same file the Siemens
// terminal on the machine is running, fetched as text and dropped into a
// srcdoc iframe, which inherits this origin — so the token in localStorage is
// already the panel's token and nothing needs CORS.
//
// What that buys is the thing the button could not do: the old one opened
// `http://192.168.1.162:5005/l75-hmi`, which exists only on the factory
// network. Through the tunnel it was a dead link over HTTPS before the request
// was even made.

// The panels are built for an industrial terminal: one wide layout meant to be
// the only thing on a screen.
//
// ── why this width is fixed, and why the frame pans ───────────────────────
//
// Squeezing the panel into a phone does not make it smaller, it makes it
// ILLEGIBLE. Its queue table is `table-layout: fixed; width: 100%` with eight
// columns, so at 375 px each column gets about 45 px and every cell wraps into a
// column of single letters. Worse, the panel's own `body` is `overflow-x:
// hidden` — so the part that does not fit is CLIPPED, with no way to reach it.
//
// So below this width the frame stops shrinking: it is rendered at the width the
// panel was drawn for and the container pans, horizontally and vertically. Above
// it, nothing changes — the frame fills the card exactly as before.
const HMI_WIDTH = 1024;

/** Until the real height is measured. */
const FRAME_MIN_HEIGHT = 900;

/** Zoom steps offered when the panel has to be panned. */
const ZOOMS = [1, 0.75, 0.5];

const MachineHmiPanel = () => {
  const { machineId = '' } = useParams();
  const mid = String(machineId).toLowerCase();
  const navigate = useNavigate();

  const [raw, setRaw] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [reloadAt, setReloadAt] = useState(0);
  const frameRef = useRef(null);

  // How wide the card actually is, so the frame can fill it when there is room
  // and stop shrinking when there is not.
  const boxRef = useRef(null);
  const [boxWidth, setBoxWidth] = useState(0);
  useEffect(() => {
    const measure = () => setBoxWidth(boxRef.current?.clientWidth || 0);
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [loading, err]);

  const [zoom, setZoom] = useState(1);

  // The panel's own height, measured from the inside.
  //
  // It has to be the frame's height rather than a guess, because a frame that
  // scrolls internally TRAPS a touch drag: the finger scrolls the panel's
  // document, which cannot scroll sideways, and the container that can never
  // receives the gesture. Sized to its content, the frame never scrolls and the
  // container does all of it.
  //
  // Readable because `srcDoc` inherits this origin — the same property that
  // gives the panel the app's token.
  const [innerHeight, setInnerHeight] = useState(0);
  const measureInner = () => {
    const doc = frameRef.current?.contentDocument;
    const h = Math.max(
      doc?.documentElement?.scrollHeight || 0,
      doc?.body?.scrollHeight || 0
    );
    if (h) setInnerHeight(h);
    return doc;
  };

  // The queue repaints every two seconds, so the height moves: a drum added to
  // the list must not end up behind the bottom edge of the frame.
  const observerRef = useRef(null);
  const onFrameLoad = () => {
    const doc = measureInner();
    if (!doc || typeof ResizeObserver === 'undefined') return;
    observerRef.current?.disconnect();
    const ro = new ResizeObserver(() => measureInner());
    ro.observe(doc.documentElement);
    observerRef.current = ro;
  };

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
    },
    []
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr('');
    http
      .get(hmiPagePath(mid), { responseType: 'text', timeout: 20000 })
      .then(({ data }) => {
        if (!alive) return;
        setRaw(typeof data === 'string' ? data : String(data ?? ''));
      })
      .catch(e => {
        if (!alive) return;
        const status = e?.response?.status;
        setErr(
          status === 404
            ? `There is no operator panel for ${mid.toUpperCase()}.`
            : e?.response?.data?.error || e?.message || 'Could not load the panel.'
        );
        setRaw('');
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [mid, reloadAt]);

  // Rewritten for THIS browser: per-port on the LAN, same-origin through the
  // tunnel. The panel's own constants are hardcoded to 192.168.1.162 — the
  // same problem config/api.js solved for the app.
  const prepared = useMemo(
    () => prepareHmiPage(raw, { redis: API.REDIS, backend: API.BACKEND }),
    [raw]
  );

  const fullscreen = () => {
    const el = frameRef.current;
    if (el?.requestFullscreen) el.requestFullscreen();
  };

  // Fill the card when it fits; stop at the panel's own width when it does not,
  // and pan instead. `boxWidth` is 0 on the first paint, which must not be read
  // as "a tiny screen" — until it is measured, assume there is room.
  const frameWidth = boxWidth ? Math.max(boxWidth, HMI_WIDTH) : HMI_WIDTH;
  const frameHeight = innerHeight || FRAME_MIN_HEIGHT;
  const panning = boxWidth > 0 && boxWidth < HMI_WIDTH;

  return (
    <Card className="mb-9 mt-3">
      <Card.Body className="pb-0">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-3 mb-3">
          <div>
            <h3 className="mb-1">
              Operator panel — {mid.toUpperCase()}
              <Badge bg="info" className="ms-2 fs-10 align-middle">
                live
              </Badge>
            </h3>
            <div className="fs-9 text-700" style={{ maxWidth: 720 }}>
              The same panel the terminal on the machine is running, not a copy of
              it. Everything it can do there it can do here — start a drum, change
              the shift, assign raw material — and unlike the old button it works
              from outside the factory network.
            </div>
          </div>

          <div className="d-flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setReloadAt(Date.now())}
              title="Fetch the panel again"
            >
              <FontAwesomeIcon icon={faRotate} className="me-1" />
              Reload
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={fullscreen}>
              <FontAwesomeIcon icon={faExpand} className="me-1" />
              Fullscreen
            </Button>
            <Button
              size="sm"
              variant="outline-info"
              onClick={() => window.open(hmiDirectUrl(mid), '_blank', 'noopener')}
              title="The panel on its own server — factory network only"
            >
              <FontAwesomeIcon icon={faUpRightFromSquare} className="me-1" />
              Open on :5005
            </Button>
            <Button
              as={Link}
              to={`/apps/machine-management/machine-queue/${mid}/edit`}
              size="sm"
              variant="phoenix-secondary"
            >
              Back to Queue Edit
            </Button>
          </div>
        </div>

        {err && (
          <Alert variant="danger" className="py-2 fs-9">
            {err}
            <Button variant="link" className="p-0 ms-2 fs-9" onClick={() => navigate(-1)}>
              go back
            </Button>
          </Alert>
        )}

        {/* A panel edited on the floor could drop or rename a constant, and it
            would then quietly go on pointing at 192.168.1.162 — working on the
            LAN and failing everywhere else, which is the failure this page
            exists to remove. Said out loud rather than left to be discovered. */}
        {!loading && !err && prepared.missing.length > 0 && (
          <Alert variant="warning" className="py-2 fs-9">
            <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
            {prepared.missing.join(', ')} could not be rewritten in this panel — those
            calls will still aim at the factory address and will fail from outside.
          </Alert>
        )}
      </Card.Body>

      <div className="px-3 pb-3" ref={boxRef}>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <div className="fs-9 text-700 mt-2">Loading the panel…</div>
          </div>
        ) : prepared.html ? (
          <>
            {panning && (
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <span className="fs-10 text-700">
                  Panoul e mai lat decât ecranul — trage în orice direcție.
                </span>
                <div className="ms-auto d-flex gap-1">
                  {ZOOMS.map(z => (
                    <Button
                      key={z}
                      size="sm"
                      variant={zoom === z ? 'primary' : 'phoenix-secondary'}
                      className="fs-10 py-0 px-2"
                      onClick={() => setZoom(z)}
                      title={
                        z === 1
                          ? 'Mărime reală — cel mai lizibil'
                          : 'Mai mult pe ecran, scris mai mic'
                      }
                    >
                      {Math.round(z * 100)}%
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* The scroller. Both axes, and the panel inside is sized to its
                own content so a drag is never swallowed by an inner scrollbar.
                Height-capped only while panning: on a wide screen the frame is
                as tall as the panel and the PAGE scrolls, exactly as before. */}
            <div
              style={{
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
                maxHeight: panning ? '78vh' : 'none',
                border: '1px solid var(--phoenix-border-color)',
                borderRadius: '0.5rem',
                background: '#fff'
              }}
            >
              {/* Carries the SCALED size, so the scrollbars match what is drawn
                  — a transform does not change layout size on its own. */}
              <div
                style={{
                  width: frameWidth * zoom,
                  height: frameHeight * zoom,
                  position: 'relative'
                }}
              >
                <iframe
                  ref={frameRef}
                  // srcDoc, not src: it inherits this origin, so localStorage —
                  // and with it the token — is already shared with the app.
                  srcDoc={prepared.html}
                  onLoad={onFrameLoad}
                  title={`${mid} operator panel`}
                  // Off ONLY once the real height is known. If the measurement
                  // ever fails, the frame keeps the inner scrollbar it used to
                  // have — a swallowed drag is annoying, content clipped at
                  // 900 px with no way to reach it is not.
                  scrolling={innerHeight ? 'no' : 'auto'}
                  style={{
                    width: frameWidth,
                    height: frameHeight,
                    transform: zoom === 1 ? undefined : `scale(${zoom})`,
                    transformOrigin: 'top left',
                    border: 0,
                    display: 'block',
                    background: '#fff'
                  }}
                />
              </div>
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );
};

export default MachineHmiPanel;
